import React, { useEffect, useState } from 'react';
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import BASE_URL from "../config";
import { toast } from 'react-toastify';
import Loader from "../components/Loader"; // Import Loader

const EditTeam = () => {
  const [formData, setFormData] = useState({
    name: "",
    department: "",
    deadline: "",
    start_date: "",
    lead: "",
    members: [],
    client: "",
    project: "", // Add project to formData
  });
  const [teamOptions, setTeamOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]); // Add state for projects
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Get team id from query param
  const searchParams = new URLSearchParams(location.search);
  const teamId = searchParams.get("id");

  // Helper to get today's date in YYYY-MM-DD format
  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Show loader while fetching
      try {
        const token = localStorage.getItem("token");

        // Fetch users
        const usersRes = await axios.get(`${BASE_URL}api/user/active-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userOptions = usersRes.data.map((user) => {
          let roleLabel = "";
          switch (user.role) {
            case "user":
              roleLabel = "Software Engineer";
              break;
            case "admin":
              roleLabel = "CTO-Admin";
              break;
            case "tester":
              roleLabel = "Tester";
              break;
            default:
              roleLabel = user.role;
          }
          return {
            value: user.id,
            text: `${user.username} - ${roleLabel}`,
            key: user.id,
          };
        });
        setTeamOptions(userOptions);

        // Fetch clients
        const clientsRes = await axios.get(`${BASE_URL}api/clients/active-clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const clientOpts = clientsRes.data.map((client) => ({
          value: client.id,
          text: client.name,
          key: client.id,
        }));
        setClientOptions(clientOpts);

        // Fetch projects (like MyTasks)
        const projectsRes = await axios.get(`${BASE_URL}api/project/find-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const projectOpts = projectsRes.data.map((project) => ({
          value: project.id,
          text: project.project_name,
          key: project.id,
        }));
        setProjectOptions(projectOpts);

        // Fetch team data by id
        if (teamId) {
          const teamRes = await axios.get(`${BASE_URL}api/team/${teamId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const team = teamRes.data;
          setFormData({
            name: team.name || "",
            department: team.department || "",
            deadline: team.deadline ? team.deadline.split("T")[0] : "",
            start_date: team.startDate ? team.startDate.split("T")[0] : "",
            lead: team.lead ? String(team.lead) : "",
            members: Array.isArray(team.members)
              ? team.members.map(String)
              : (team.members || "").split(",").filter(Boolean),
            client: team.client ? String(team.client) : "",
            project: team.project ? String(team.project) : "", // Add project to formData
          });
        }
      } catch (error) {
        toast.error("Error fetching data for edit team.");
      } finally {
        setLoading(false); // Hide loader after fetch
      }
    };
    fetchData();
  }, [teamId]);

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;
    if (type === "select-multiple") {
      const selectedValues = Array.from(selectedOptions).map((option) => option.value);
      setFormData({
        ...formData,
        [name]: selectedValues,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const { name, department, deadline, start_date, lead, members, client, project } = formData;
    let formErrors = {};
    let isValid = true;

    if (!name) {
      formErrors.name = "Team name is required";
      isValid = false;
    }
    if (!department) {
      formErrors.department = "project is required";
      isValid = false;
    }
    if (!start_date) {
      formErrors.start_date = "Project start date is required";
      isValid = false;
    }
    if (!deadline) {
      formErrors.deadline = "Deadline is required";
      isValid = false;
    }
    if (!lead) {
      formErrors.lead = "Team lead is required";
      isValid = false;
    }
    if (!members || members.length === 0) {
      formErrors.members = "At least one member is required";
      isValid = false;
    }
    if (!client) {
      formErrors.client = "Client is required";
      isValid = false;
    }
    // if (!project) {
    //   formErrors.project = "Project is required";
    //   isValid = false;
    // }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log("Validation failed", errors);
      return;
    }

    // Only use the fields needed by the backend
    const { name, department, deadline, start_date, lead, members } = formData;

    const payload = {
      name,
      department,
      deadline,
      start_date,
      lead: Number(lead),
      members: members.join(","),
      // Do NOT include project here
    };

    // Log only the payload, not formData
    console.log("Sending update request", payload);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${BASE_URL}api/team/update/${teamId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Team updated successfully!");
      navigate("/teams");
    } catch (error) {
      console.error("Update error", error);
      toast.error("Failed to update team.");
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <Loader message="Loading team data..." />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Edit Team</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="team-name"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("team-name")?.focus()}
          >
            Team Name
          </label>
          <input
            id="team-name"
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className="text-danger">{errors.name}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="start-date"
            style={{ cursor: "pointer" }}
            onMouseDown={e => {
              e.preventDefault();
              const input = document.getElementById("start-date");
              if (input) input.showPicker ? input.showPicker() : input.focus();
            }}
          >
            Project Start Date
          </label>
          <input
            id="start-date"
            type="date"
            className="form-control"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
          />
          {errors.start_date && <p className="text-danger">{errors.start_date}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="deadline"
            style={{ cursor: "pointer" }}
            onMouseDown={e => {
              e.preventDefault();
              const input = document.getElementById("deadline");
              if (input) input.showPicker ? input.showPicker() : input.focus();
            }}
          >
            Deadline
          </label>
          <input
            id="deadline"
            type="date"
            className="form-control"
            name="deadline"
            value={formData.deadline}
            onChange={handleChange}
          />
          {errors.deadline && <p className="text-danger">{errors.deadline}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="department"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("department")?.focus()}
          >
            Project
          </label>
          <select
            id="department"
            className="form-select"
            name="department"
            value={formData.department}
            onChange={handleChange}
          >
            <option value="">Select Project</option>
            {projectOptions.map((project) => (
              <option key={project.key} value={project.value}>
                {project.text}
              </option>
            ))}
          </select>
          {errors.department && <p className="text-danger">{errors.department}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="lead"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("lead")?.focus()}
          >
            Team Lead
          </label>
          <select
            id="lead"
            className="form-select"
            name="lead"
            value={formData.lead}
            onChange={handleChange}
          >
            <option value="">Select Team Lead</option>
            {teamOptions.map((option) => (
              <option key={option.key} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
          {errors.lead && <p className="text-danger">{errors.lead}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="members"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("members")?.focus()}
          >
            Members
          </label>
          <select
            id="members"
            className="form-select"
            name="members"
            multiple
            value={formData.members}
            onChange={handleChange}
          >
            {teamOptions
              .filter((option) => !formData.lead || option.value !== Number(formData.lead))
              .map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
          </select>
          {errors.members && <p className="text-danger">{errors.members}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="client"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("client")?.focus()}
          >
            Client
          </label>
          <select
            id="client"
            className="form-select"
            name="client"
            value={formData.client}
            onChange={handleChange}
            disabled // Client is not updatable per backend API
          >
            <option value="">Select Client</option>
            {clientOptions.map((client) => (
              <option key={client.key} value={client.value}>
                {client.text}
              </option>
            ))}
          </select>
          {errors.client && <p className="text-danger">{errors.client}</p>}
        </div>

        <button type="submit" className="btn btn-primary">
          Update Team
        </button>
      </form>
    </div>
  );
};

export default EditTeam;
